
import Vue from 'vue';
import type { PropType } from 'vue';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import { ErrorResponse } from '~/types/api_helper';

import TRUCKS from '@/store/modules/TruckModule';
import CART from '@/store/modules/CartModule';
import MISC_DATA from '@/store/modules/MiscDataModule';

import CalendarSlotNew from '@/components/general/calendar/SlotNew.vue';

dayjs.extend(weekOfYear);
dayjs.extend(isoWeeksInYear);

export default Vue.extend({
	name: 'SlotGridNew',

	components: { CalendarSlotNew },

	props: {
		truckId: {
			type: String,
			required: true
		},

		truckLocation: {
			type: String,
			required: true
		},

		autoSelect: {
			type: Boolean,
			default: false
		},

		resetCalendarPage: {
			type: Number,
			default: 0
		},

		triggerClick: {
			type: Object as PropType<{
				triggerClick: Number;
				triggerId: String;
			}>,
			default: () => {
				return { triggerClick: 0, triggerId: '' };
			}
		},

		isSingleTruck: {
			type: Boolean,
			default: false
		},

		allSlotsInDialog: {
			type: Array as PropType<{ truck_id: string; slots: number[] | ErrorResponse }[]>,
			default: () => []
		},

		nonWorkingSlots: {
			type: Object as PropType<{ truck_id: string; non_working_slots: number[] }>,
			default: () => ({ truck_id: '', non_working_slots: [] })
		}
	},

	data() {
		return {
			dayjs: dayjs,
			start_date: dayjs().format('YYYY-MM-DD').concat('T00:00'),
			end_date: dayjs().add(8, 'weeks').format('YYYY-MM-DD').concat('T00:00'),
			unavailable_slots: [] as number[],
			selected_slots: [] as number[],
			non_working_slots: [] as number[],
			isRangeSelectionInProgress: false,

			current_page: 0,
			weeks_requested: 8,

			loading: false,
			currentDate: dayjs()
		};
	},

	computed: {
		TRUCKS: () => TRUCKS,
		CART: () => CART,
		MISC_DATA: () => MISC_DATA,

		isMobile(): boolean {
			return this.$vuetify.breakpoint.smAndDown;
		},

		time_row() {
			if (this.isMobile) {
				return ['', '00:00<br>-<br>05:59', '06:00<br>-<br>11:59', '12:00<br>-<br>17:59', '18:00<br>-<br>23:59'];
			}

			return [' ', '00:00 - 05:59', '06:00 - 11:59', '12:00 - 17:59', '18:00 - 23:59'];
		},

		promoOneSlotIsActive(): boolean {
			if (!this.MISC_DATA.basedata.features) {
				return false;
			}
			return this.MISC_DATA.basedata.features['6_hour_blocks'] === true;
		},

		peakTimeSlots(): number[] {
			const slots = this.MISC_DATA.basedata.features.peaktime_slots;
			if (!this.MISC_DATA.basedata.features && !slots) {
				return [];
			}
			return this.MISC_DATA.basedata.features.peaktime_slots;
		},

		peakTime2Slots(): number[] {
			const slots = this.MISC_DATA?.basedata?.features.peaktime2_slots;
			if (!this.MISC_DATA.basedata.features && !slots) {
				return [];
			}
			return this.MISC_DATA.basedata.features.peaktime2_slots;
		},

		offTimeSlots(): number[] {
			const slots = this.MISC_DATA.basedata.features.offtime_slots;
			if (!this.MISC_DATA.basedata.features && !slots) {
				return [];
			}
			return this.MISC_DATA.basedata.features.offtime_slots;
		},

		daysAmount(): number {
			return 7;
		},

		selectionStart(): string | undefined {
			if (this.selected_slots.length < 1) {
				return;
			}
			const start_index = Math.min(...this.selected_slots);
			return this.translateToDateTime(start_index);
		},

		selectionEnd(): string | undefined {
			if (!this.selectionStart) {
				return;
			}
			if (this.selected_slots.length === 1) {
				return dayjs(this.selectionStart).add(359, 'minute').format('YYYY-MM-DDTHH:mm');
			}
			return this.translateToDateTime(Math.max(...this.selected_slots), true);
		},

		currentWeekDates(): string[] {
			let starting_date = dayjs(JSON.parse(JSON.stringify(this.start_date))).format();
			const end_date = dayjs(JSON.parse(JSON.stringify(this.end_date))).format();
			const res = [];
			// Create a full array of dates from start to end
			while (starting_date <= end_date) {
				res.push(starting_date);
				starting_date = dayjs(starting_date).add(1, 'day').format();
			}
			// Return the current chunked week using the current_page
			const chunk_start = this.current_page > 0 ? this.daysAmount * this.current_page : 0;
			return res.slice(chunk_start, chunk_start + 7);
		}
	},

	watch: {
		async truckId() {
			this.reset();
			await this.fetchSlots();
		},
		// Handle the emits for start and end // clear
		selected_slots(newVal: any, _oldVal: any) {
			if (newVal.length === 1) {
				// Set flag that range selection is in progress
				this.isRangeSelectionInProgress = true;
			} else if (newVal.length > 1) {
				// Selection is complete, reset flag
				this.isRangeSelectionInProgress = false;
			} else {
				// No selection, reset flag
				this.isRangeSelectionInProgress = false;
			}

			if (this.selected_slots.length > 0) {
				// here handle the slot
				this.$emit('slot-times', { start: this.selectionStart, end: this.selectionEnd });
			} else {
				// clear stored slot
				this.$emit('clear-selection');
			}
		},

		triggerClick: {
			handler(newVal, _oldVal) {
				this.$nextTick(() => {
					if (newVal.triggerClick && newVal.triggerId === this.truckId) {
						this.emitDataToCarousel();
					}
				});
			},
			deep: true
		},

		resetCalendarPage(newVal: Number, oldVal: Number) {
			if (newVal !== oldVal) {
				this.current_page = 0;
				this.selected_slots = [];
			}
		},

		current_page: {
			handler() {
				this.$emit('updateWeekDates', this.currentWeekDates);
			},
			immediate: true
		}
	},

	async mounted() {
		if (this.isSingleTruck) {
			await this.fetchCurrentTruckSlots();
		} else {
			await this.fetchSlots();
		}
	},

	methods: {
		fetchSlots(): void {
			this.$logger.console({ message: `fetch calendar slots - location: ${this.truckLocation}` });

			const currentSlotItem = this.allSlotsInDialog.find((item) => item.truck_id === this.truckId);
			let slots: number[] | undefined;

			if (currentSlotItem && Array.isArray(currentSlotItem.slots)) {
				slots = currentSlotItem.slots;
			}

			if (!slots) {
				return;
			}

			let i = 0;
			// Check for past slots in the first day and add them to unavailable slots
			while (i <= 3) {
				if (dayjs(this.translateToDateTime(i, true)) < dayjs()) {
					slots.push(i);
				}
				i++;
			}
			this.unavailable_slots = slots;
			if (this.weeks_requested < 16) {
				this.storeFirstWeekSlotStatus();
			}
			if (this.autoSelect) {
				this.selectFirst4Slots();
			}
		},
		// Use to emit the first week slots status for the GTM event
		storeFirstWeekSlotStatus(): void {
			// only take slots on the first week (4 * 7 days = 28 slots in a 0 based array)
			const first_week_unavailable_slots = this.unavailable_slots.filter((s) => s < 27);
			const unavailable_slots_amount = first_week_unavailable_slots.length;
			// difference to 4 * 7 days
			const available_slots_amount = 28 - unavailable_slots_amount;
			// Save in truck module {id: truck, blocked_slots: number, available_slots: number}
			this.TRUCKS.storeCurrentWeekSlotsStatus({
				id: this.truckId,
				blocked_slots: unavailable_slots_amount,
				available_slots: available_slots_amount
			});

			const payload = {
				id: this.truckId,
				blocked_slots: unavailable_slots_amount,
				available_slots: available_slots_amount
			};

			this.$emit('slotsPayload', payload);
		},

		warpIndex(index: number): number {
			return index + this.daysAmount * 4 * this.current_page;
		},

		slotClicked(index: number): void {
			const selected = JSON.parse(JSON.stringify(this.selected_slots));
			// More than 1 index in selected_slots -> empty and set this one.
			if (selected.length > 1) {
				this.selected_slots = [index];
				// Starting a new selection
				this.isRangeSelectionInProgress = true;
				return;
			}
			// Only 1 slot in selected_slots.
			if (selected.length === 1) {
				if (selected.includes(index)) {
					// It's the same -> empty (unselect).
					this.selected_slots = [];
					this.isRangeSelectionInProgress = false;
				} else if (selected[0] > index) {
					// it's in the past -> replace
					this.selected_slots = [index];
					this.isRangeSelectionInProgress = true;
				} else {
					// Process range.
					this.selectRange(selected[0], index);
					this.isRangeSelectionInProgress = false;
				}
				return;
			}
			// Nothing selected
			this.selected_slots.push(index);
			this.isRangeSelectionInProgress = true;
		},

		nonWorkingSlotClicked(index: number) {
			// We will keep track of the clicked non-working slot
			// but not directly modify the selection
			// Instead, we'll emit an event to trigger the API call
			// which will handle the error cases
			this.$emit('non-working-slot-clicked', index);
		},

		async selectRange(start: number, end: number) {
			const maxDaysAllowed = this.MISC_DATA.basedata.features?.booking_max_length || 0;
			const maxSlotsAllowed = maxDaysAllowed * 4 - 1;

			// Get non-working slots
			// const nonWorkingSlots = this.nonWorkingSlots?.non_working_slots || [];

			// Reset selections
			let selection = [];

			// If start and end are the same, no need to proceed
			if (start === end) {
				this.selected_slots = [start];
				return;
			}

			// If the user selected a range in reverse order (end is before start)
			if (end < start) {
				// Swap them
				const temp = start;
				start = end;
				end = temp;
			}

			// First, collect all slots within the range (including non-working but excluding booked/unavailable)
			for (let current_index = start; current_index <= end; current_index++) {
				// Do not include unavailable/booked slots
				if (!this.unavailable_slots.includes(current_index)) {
					selection.push(current_index);
				}
			}

			// If we don't have any valid slots, check if we can at least use the end point
			if (selection.length === 0) {
				if (!this.unavailable_slots.includes(end)) {
					selection = [end];
				} else if (!this.unavailable_slots.includes(start)) {
					selection = [start];
				}
			}

			// Check if the selection exceeds the maximum allowed days
			if (maxDaysAllowed !== 0 && selection.length > maxSlotsAllowed) {
				const truncatedRange = selection.slice(0, maxSlotsAllowed);
				this.selected_slots = truncatedRange;
				await this.$dialogs.alert(
					this.$t('cust.alert.max_booking_duration.text', { max_days: maxDaysAllowed }) as string,
					this.$t('cust.alert.max_booking_duration.title') as string
				);
				return;
			}

			this.selected_slots = selection;
		},

		selectFirst4Slots(): void {
			// Get non-working slots
			const nonWorkingSlots = this.nonWorkingSlots?.non_working_slots || [];

			let i = 0;
			while (i < 28) {
				// Skip unavailable and non-working slots
				if (this.unavailable_slots.includes(i) || nonWorkingSlots.includes(i)) {
					if (this.selected_slots.length > 0) {
						break;
					}
					i++;
					continue;
				}

				if (this.selected_slots.length >= 4) {
					break;
				}

				this.selected_slots.push(i);
				i++;
			}
		},

		// Method to calculate date and time of an index
		translateToDateTime(index: number, end?: boolean): string {
			const time_slots = end ? ['05:59', '11:59', '17:59', '23:59'] : ['00:00', '06:00', '12:00', '18:00'];
			// By using the modulus of the index by 4, we get the index of the col in a row.
			const col_pos = index % 4;
			// We can therefore use it in time_slots to get the corresponding time.
			const time = time_slots[col_pos];
			// Substracting the col index from the index will give us the multiple of 4 from this row (1st slot).
			// Divided by 4 to get number of days to add from the start date.
			const days_from_start = (index - col_pos) / 4;
			const start_date = dayjs(this.start_date).format('YYYY-MM-DD');

			return dayjs(start_date).add(days_from_start, 'days').format('YYYY-MM-DD').concat(`T${time}`);
		},

		isPromoSlotCheck(promo_slots: number[]): any {
			let day_index = dayjs(this.selectionStart).day() - 1;
			const hour = dayjs(this.selectionStart).hour();
			let hour_index = 0;
			switch (hour) {
				case 0:
					hour_index = 0;
					break;
				case 6:
					hour_index = 1;
					break;
				case 12:
					hour_index = 2;
					break;
				case 18:
					hour_index = 3;
			}
			if (day_index < 0) {
				// case when sunday cause it's 0 by default
				day_index = 6;
			}
			const index = day_index * 4 + hour_index;
			return promo_slots.some((slot: number) => index === slot - 1); // -1 cause monday is 1 in the promo lists
		},

		async nextPage(): Promise<void> {
			if (this.current_page >= this.weeks_requested - 1) {
				// Buffer
				// Request 8 more weeks
				this.end_date = dayjs(JSON.parse(JSON.stringify(this.end_date)))
					.add(8, 'weeks')
					.format('YYYY-MM-DD')
					.concat('T00:00');
				this.weeks_requested += 8;
				await this.fetchCurrentTruckSlots();
			}
			this.current_page++;
			this.currentDate = this.currentDate.add(1, 'week');
			// Emit event to parent to update page tracking
			this.$emit('nextPage');

			// We don't want to clear the selection when changing pages anymore
			// Let the user maintain their selection when navigating
		},

		async prevPage(): Promise<void> {
			if (this.current_page <= 0) {
				// Don't go below page 0
				return;
			}

			this.current_page--;
			this.currentDate = this.currentDate.subtract(1, 'week');

			// Ensure data is available for the previous page
			// This is important if the user had gone forward extensively and then back
			await this.fetchCurrentTruckSlots();

			// Emit event to parent to update page tracking
			this.$emit('prevPage');

			// We don't want to clear the selection when changing pages anymore
			// Let the user maintain their selection when navigating
		},

		reset() {
			this.selected_slots = [];
			this.weeks_requested = 8;
			this.end_date = dayjs().add(8, 'weeks').format('YYYY-MM-DD').concat('T00:00');
		},

		emitDataToCarousel() {
			const payload = {
				id: this.truckId,
				blocked_slots: this.unavailable_slots.filter((s) => s < 27).length,
				available_slots: 28 - this.unavailable_slots.filter((s) => s < 27).length
			};

			this.$emit('carouselData', payload);
		},

		updateNonWorkingSlots(nonWorkingSlots: number[]) {
			// When page changes, this method will be called to update the non-working slots
			// that should be displayed on the calendar
			if (Array.isArray(nonWorkingSlots)) {
				// Update the component's internal reference to non-working slots
				// We don't need to modify the prop as it's already updated in the parent
				// and will be passed down correctly

				// Force a re-render of the component to reflect the updated slots
				this.$forceUpdate();
			}
		},

		async fetchCurrentTruckSlots(): Promise<void> {
			const res = await this.TRUCKS.FETCH_TRUCK_SLOTS([
				{
					truck_id: this.truckId,
					truck_location: this.truckLocation,
					start: this.start_date,
					end: this.end_date
				}
			]);

			if (res instanceof ErrorResponse) {
				return;
			}

			const singleItem = res[0];

			if (Array.isArray(singleItem.slots)) {
				let i = 0;

				while (i <= 3) {
					if (dayjs(this.translateToDateTime(i, true)) < dayjs()) {
						singleItem.slots.push(i);
					}
					i++;
				}
				this.unavailable_slots = singleItem.slots;
			}

			if (this.weeks_requested < 16) {
				this.storeFirstWeekSlotStatus();
			}
			if (this.autoSelect) {
				this.selectFirst4Slots();
			}
		}
	}
});
